import type { TextToSpeechOptions, TextToSpeechResponse } from '~/models/Brikke/TextToSpeech'
import { sleep } from '~/utils/asyncUtils'

export const TTS_MAX_RETRIES = 40
export const TTS_RETRY_INTERVAL_MS = 5000
export const TTS_CDN_HOST = import.meta.env.VITE_BEYONDWORDS_CDN

export function extractAudioUrl(response: TextToSpeechResponse): string | null {
  if (response.status !== 'processed' || !response.audio) {
    return null
  }
  const audioEntry = response.audio.find(entry => entry.content_type === 'audio/mpeg')
  return audioEntry?.url ?? null
}

export function rewriteTtsCdnUrl(url: string): string {
  return url.replace('cdn.beyondwords.io', TTS_CDN_HOST)
}

export interface GenerateTtsResult {
  url: string | null
  error: Error | null
}

export async function generateTtsWithRetry(
  textToSpeech: (options: TextToSpeechOptions) => Promise<TextToSpeechResponse>,
  options: TextToSpeechOptions,
  onRetry?: (attempt: number) => void,
): Promise<GenerateTtsResult> {
  for (let attempt = 0; attempt <= TTS_MAX_RETRIES; attempt++) {
    try {
      const response = await textToSpeech(options)

      if (response.status === 'error') {
        return { url: null, error: new Error('TTS generation failed') }
      }

      const audioUrl = extractAudioUrl(response)
      if (audioUrl) {
        return { url: rewriteTtsCdnUrl(audioUrl), error: null }
      }

      if (attempt < TTS_MAX_RETRIES) {
        onRetry?.(attempt + 1)
        await sleep(TTS_RETRY_INTERVAL_MS)
      }
    } catch (error) {
      return { url: null, error: error instanceof Error ? error : new Error('Unknown error') }
    }
  }

  return { url: null, error: new Error(`Timed out after ${TTS_MAX_RETRIES} retries`) }
}
